<template>
	<div>
		<ModalBase
			container-class="rounded-lg text-primary bg-black border-2 border-primary p-4 sm:p-8 w-full md:w-5/6"
			:clickOutsideEnabled="true"
			ref="seatPreviewPopup">
			<div class="pb-2 h-[86vh] md:h-[75vh] flex flex-col text-center">
				<div class="font-serif text-3xl">
					{{ $t("seatsIoPreviewTitle") }}
				</div>

				<div
					class="flex flex-col justify-between grow max-md:landscape:overflow-y-auto sm:landscape:overflow-y-auto overflow-x-hidden">
					<div class="pt-4">
						<div class="italic text-sm">{{ $t("seatsIoPreviewHelp") }}</div>
					</div>

					<div class="text-right py-2">
						<ButtonIconThenText
							class="text-ternary text-xs hoverEffects viewDetails"
							:text="showHelp ? $t('helpHide') : $t('helpShow')"
							icon="fa-solid fa-circle-info"
							@clicked="expandHelp" />
					</div>

					<transition name="slide-fade">
						<div
							v-if="showHelp"
							class="text-sm pt-2 text-left"
							:class="{ 'leading-3': !$utilities.$isMobileDevice() }">
							<div
								v-html="
									$utilities.$isMobileDevice() ? t('seatsIoPreviewHelpMobile') : t('seatsIoPreviewHelpDesktop')
								"></div>
						</div>
					</transition>

					<div
						id="seatChart"
						class="grow"></div>

					<div class="py-4 md:pb-0">
						<ButtonPrimary
							class="w-64 !rounded-lg"
							@click="closeModal">
							Close
						</ButtonPrimary>
					</div>
				</div>
			</div>
		</ModalBase>
	</div>
</template>

<script setup>
	import * as Sentry from "@sentry/browser"
	import { SHOW_SEAT_PREVIEW_POPUP } from "assets/events/cart.js"
	import { useRefsStore } from "~/stores/RefsStore"
	import { SHOW_ERROR } from "assets/events/errors.js"

	const { seatsIoWorkspaceKey, seatsIoEvent } = defineProps({
		seatsIoWorkspaceKey: String,
		seatsIoEvent: String,
	})

	const { t } = useI18n()
	const { $eventBus, $messages, $utilities } = useNuxtApp()
	const refsStore = useRefsStore()
	const runtimeConfig = useRuntimeConfig()
	const seatPreviewPopup = ref(null)

	const showHelp = ref(false)
	const expandHelp = () => {
		showHelp.value = !showHelp.value
	}

	const setupSeatsChart = () => {
		//Seats.IO SDK is imported in the nuxt.config
		try {
			let seatsObject = new seatsio.SeatingChart({
				session: "none",
				divId: "seatChart",
				workspaceKey: seatsIoWorkspaceKey,
				event: seatsIoEvent,
				mode: "static",
				loading: `<div class="w-32 h-32"><img src="${runtimeConfig.public.DEFAULT_LOADER}" alt="loading"/></div>`,
				fitTo: "widthAndHeight",
				colors: {
					colorSelected: "#a2a2a2",
				},
				showMinimap: !$utilities.$isMobileDevice(),
				showFullScreenButton: false, //Full screen button causes rendering issues when un-fullscreening
				popoverInfo: (object) => $messages.$t("seatsIoLabel", { id: object.label }),
				showSeatLabels: true,
				objectPopover: { showLabel: false },
				//Do not set the default maxSelectedObjects, otherwise seat selections get lost. Use the onChartRendered event handler instead.
				onChartRenderingFailed: () => {
					logErrorAndCloseModal("onChartRenderingFailed")
				},
			})
			refsStore.setSeatsPreviewObjectRef(seatsObject)
			seatsObject.render()
		} catch (e) {
			logErrorAndCloseModal("Error loading chart", e)
		}
	}

	const openModal = () => {
		refsStore.seatPreviewPopupRef.openModal()

		if (refsStore.seatsPreviewObjectRef) {
			refsStore.seatsPreviewObjectRef.rerender()
		} else {
			setupSeatsChart()
		}
	}

	const resetCacheAndClose = () => {
		refsStore.seatPreviewPopupRef.closeModal()
	}

	const closeModal = () => {
		resetCacheAndClose()
	}

	const logErrorAndCloseModal = (errMsg, exception) => {
		console.error(exception)
		Sentry.captureMessage(`Seats IO Error - ${errMsg}: ${exception ? exception.message : "No Exception"}`, "error")
		resetCacheAndClose()

		$eventBus.$emit(SHOW_ERROR, {
			titleText: $messages.$t("seatsIoErrorPopupTitle"),
			bodyText: $messages.$t("seatsIoErrorPopupBody"),
		})
	}

	onMounted(() => {
		refsStore.setSeatPreviewPopupRef(seatPreviewPopup.value)

		$eventBus.$on(SHOW_SEAT_PREVIEW_POPUP, openModal)
	})

	onUnmounted(() => {
		$eventBus.$off(SHOW_SEAT_PREVIEW_POPUP)
	})
</script>

<style lang="scss" scoped>
	#seatChart {
		@apply py-4;
		@apply h-[10em];
	}
</style>
